import { Injectable } from '@angular/core';
import { IFeatureFlag, IFeatureFlagsPerEnvironment } from '../../interfaces/IFeatureFlags';
import { IApiClaimsHistory, IContract, IContractData } from '../../interfaces/IJob';
import { ISearchContractResponse } from '../../interfaces/ISearchContract';
import {
  CE_PLAN,
  CE_CUSTOMER_PHONE,
  BYOP_CUSTOMER_PHONE,
  TXU_CUSTOMER_PHONE,
  CINCH_CUSTOMER_PHONE,
  REMAX_CUSTOMER_PHONE,
  REMAX_LOGO_ID,
  BYOP_PLAN,
  TXUCE_PLAN,
  WARRANTY_SHORT_LABEL,
  YES_RESPONSE,
  CLIENT_CONTACT_NUMBERS,
  LF_CLIENT,
  LF_CLIENT_CONTACT_NUMBER,
  EMPLYD_CLIENT,
  PAYMENT_IN_PROCESS_STATUS,
  BALANCE_DUE_STATUS,
  BALANCE_DUE_MESSAGE,
  NO_DUE_MESSAGE,
  CHARGES_PAID_MESSAGE,
  NEXT_PAYMENT_MESSAGE,
  SRG_PLANS,
  BYOP_SURCHARGE_TEXT_FOR_MULTI_ITEMS,
  SURCHARGE_TEXT_FOR_MULTI_ITEMS,
  CONTRACT_STATUS_ACTIVE,
  ALLOWED_ADD_COVERAGE_DAYS,
  CLIENT_ID,
  SRG,
  TXSRG,
  TEXT_PREVENTATIVE_MAINTENANCE,
  APOSP_ACCOUNT_NUMBERS,
  ARCHMI,
  EMBED,
  CLIENT_MADECC,
  BILLONCE,
  BILLCLIENT,
  ALLSTATE,
  PAYMENTECH,
  ACH_AUTO,
  SPECIAL_BENEFITS_CONTACT_NUMBERS,
  CAN,
  CMP,
  SRCAND,
  SRCMPD,
  PROCURE_VENDOR_MESSAGE,
  COMPLETED_MESSAGE,
  CANCELLED_MESSAGE,
  BCW,
  BDW,
  TCAPI_WEB,
  CSVF10105,
  CSVK_CODES,
  DisplayStatus,
  PENDING_REPLACEMENT_DECISION_SUBSTATUS,
  CLAIM_CREDIT_ACCEPTED_SUBSTATUS,
  REVIEW_DIAGNOSIS_STATUS_CODE,
  CLAIM_CREDIT_PART_DESCRIPTION,
  ClientIdForLogos,
} from '../../utils/constants';
import { APOSP_CANCEL_BUTTON_ENABLED, FlagCeEnabled, MADE_CREDIT_CARD_ENABLED, ROOF_LEAK_ENABLED } from '../../utils/feature-flags';
import { IADServiceAgainJob, IBalance } from '../../interfaces/IUtilInterfaces';
import { BehaviorSubject } from 'rxjs';
import { IAvailabilityDate, ISelectedProduct } from '../../components/pages/service-request/service-request.interface';
import { CONTRACT_STATUS_CANCELLATION_SCHEDULED, CONTRACT_STATUS_CANCELLED, CONTRACT_STATUS_INACTIVE, CONTRACT_STATUS_UPPER_CANCELED, CONTRACT_STATUS_UPPER_EXPIRED, CONTRACT_STATUS_UPPER_INACTIVE, CONTRACT_STATUS_TITLE_PLAN_CANCELLED, CONTRACT_STATUS_TITLE_PLAN_EXPIRED, HMS_CLIENT, HMS_CLIENT_LIFERAY_FEATURE, LIFERAY_FEATURE_OFF, MADE_CREDIT_CARD_LIFERAY_FEATURE, MOEN_CLIENT, MOEN_LIFERAY_FEATURE, NORMAL_CLIENT_LIFERAY_FEATURE, SIRVA_CLIENT, SIRVA_LIFERAY_FEATURE, STATE_CLIENT_LIFERAY_FEATURE_PREFIX } from '../../components/pages/my-plan/my-plan.constants';
import { formatDate } from '@angular/common';
import { IOperationItem } from '../../interfaces/IOpertationItem';
import { Model } from '../../components/pages/claim-history/claim-history.interface';
import { environment } from '../../../environments/environment';
import { HOME_ELCTRNCS, HOME_ELECTRONICS } from '../../components/shared/special-benefits/special-benefits.constants';
import { FEATURES_CONSTANT } from '../../utils/features.constant';

@Injectable({
  providedIn: 'root'
})
export class UtilitiesService {
  screenWidth$ = new BehaviorSubject<number>(window.innerWidth);
  constructor() {
    window.addEventListener('resize', () => {
      this.screenWidth$.next(window.innerWidth);
    });
  }

  public getContactNumber(contractInfo: string | IContract | ISearchContractResponse): string {
    const { clientId, coverageGroup = '', franchiseCode = '', productTypeCode = '', referralCoverageFlag = '', referralCoverageGroup = '', logoId = 0 } =
      typeof contractInfo === 'string' ? { clientId: contractInfo } : contractInfo;

    if (this.checkForCE(productTypeCode || '', referralCoverageFlag || '', referralCoverageGroup || '')){
        return CE_CUSTOMER_PHONE;
    }
    if (this.isPlanRemax(logoId)) {
      return REMAX_CUSTOMER_PHONE;
    }
    if (this.isPlanBYOP(coverageGroup)) {
      return BYOP_CUSTOMER_PHONE;
    }
    if (this.isPlanTxuCE(coverageGroup)) {
      return TXU_CUSTOMER_PHONE;
    }
    if (this.isPlanCE(coverageGroup)) {
      return CE_CUSTOMER_PHONE;
    }
    if (franchiseCode === LF_CLIENT) {
      return LF_CLIENT_CONTACT_NUMBER;
    }

    return CLIENT_CONTACT_NUMBERS[clientId.toUpperCase()] || CINCH_CUSTOMER_PHONE
  }

  private checkForCE(productTypeCode: string, referralCoverageFlag: string, referralCoverageGroup: string): boolean{
    return  productTypeCode == WARRANTY_SHORT_LABEL &&
    referralCoverageFlag === YES_RESPONSE &&
    referralCoverageGroup == CE_PLAN
  }

  public isPlanRemax(logoId: number): boolean {
    return logoId === REMAX_LOGO_ID;
  }

  public isPlanBYOP(coverageGroup: string): boolean {
    return coverageGroup === BYOP_PLAN;
  }

  public isPlanCE(coverageGroup: string): boolean {
    return FlagCeEnabled && coverageGroup === CE_PLAN;
  }

  public isPlanTxuCE(coverageGroup: string): boolean {
    return coverageGroup === TXUCE_PLAN;
  }

  public isPlanEMPLYD(clientId: string): boolean {
    return clientId === EMPLYD_CLIENT;
  }

  public isPlanSurge(coverageGroup: string): boolean {
    if (typeof coverageGroup !== 'string') {
      return false;
    }
    return SRG_PLANS.includes(coverageGroup);
  }

  public isAPOSPJob(serviceProviderAccount: string): boolean {
    return APOSP_ACCOUNT_NUMBERS.includes(serviceProviderAccount);
  }

  public isProcureVendorJob(serviceProviderAccount: string): boolean {
    return environment.procureVendorServiceProvider === serviceProviderAccount;
  }

  public isRoofLeakJob(serviceProviderAccount: string): boolean {
    return ROOF_LEAK_ENABLED && environment.roofLeakServiceProvider === serviceProviderAccount;
  }

  public isAutomaticDecisionJob(serviceProviderAccount?: string | null): boolean {
    return !!serviceProviderAccount && environment.automaticDecision.serviceProvider === serviceProviderAccount;
  }

  /**
   * @description For AD jobs, Service again is hidden when the customer chose claim credit (even after
   * the job is completed) or while the replacement decision is still pending (See options).
   * Replacement jobs fall back to BAU recall eligibility.
   */
  public isADServiceAgainRestricted(job: IADServiceAgainJob): boolean {
    const { jobDisplay } = job;
    const isClaimCreditSelected = job.serviceJobSubStatus === CLAIM_CREDIT_ACCEPTED_SUBSTATUS ||
      jobDisplay?.displayStatusCode === DisplayStatus.claimCreditAccepted ||
      !!jobDisplay?.claimCreditInfo ||
      !!job.parts?.some(part => part?.partDescription?.trim().toLowerCase() === CLAIM_CREDIT_PART_DESCRIPTION);
    if (isClaimCreditSelected) {
      return true;
    }

    // "See options" is only offered before the job is completed
    return job.serviceJobStatus !== CMP && (
      job.serviceJobSubStatus === PENDING_REPLACEMENT_DECISION_SUBSTATUS ||
      (jobDisplay?.displayStatusCode === REVIEW_DIAGNOSIS_STATUS_CODE && !!jobDisplay?.claimOptions?.hasOptions)
    );
  }

  /**
   * @description Single entry point for the AD Service again rule, so every screen behaves the same.
   */
  public isServiceAgainRestrictedForADJob(serviceProviderAccount: string | null | undefined, job: IADServiceAgainJob): boolean {
    return this.isAutomaticDecisionJob(serviceProviderAccount) && this.isADServiceAgainRestricted(job);
  }
  
  public formatPhone(value: string): string {
    //transforms any phone number value to (xxx) xxx-xxxx
    if (value) {
      let s2 = ('' + value).replace(/\D/g, '');
      let m = s2.match(/^(\d{3})(\d{3})(\d{4})$/);
      return !m ? '' : '(' + m[1] + ') ' + m[2] + '-' + m[3] || '';
    }
    return '';
  }

  public formatPhoneNumberinStringasAnchortag(value: string, isUnderline = true): string {
    return value.replace(
      /\(?([0-9]{3})\)?[- ]?([0-9]{3})[- ]?([0-9]{4})/,
      "<a href='tel:$1$2$3' class='text-blue-700 " + (isUnderline ? 'underline' : 'no-underline') + "'>($1)-$2-$3</a>"
    );
  }

  public formatPhoneNumberInString(value: string): string {
     // Formats phone numbers in a string to (xxx)-xxx-xxxx pattern without creating anchor tags
    return value.replace(
      /\(?([0-9]{3})\)?[- ]([0-9]{3})[- ]([0-9]{4})/,
      '($1)-$2-$3'
    );
  }

  public cloneDeep(obj: any): any {
    return (obj) ? JSON.parse(JSON.stringify(obj)) : null;
  }

  public formatStrDate(dateString: string): string {
      if (dateString.length !== 8) return ''; // Validate input length

      const year = dateString.substring(0, 4);
      const month = dateString.substring(4, 6);
      const day = dateString.substring(6, 8);

      return `${month}/${day}/${year}`;
  }

  public getBalanceOnFormat( contract: IContractData ): IBalance {
    let status = '';
    let msg = '';
    let makePayment = false;
    let editPaymentInfo = false;
    let msgDate = 0;

    if ( contract.userStatuses.PAYMENT_PENDING ) {
        status = PAYMENT_IN_PROCESS_STATUS;
        msg = NO_DUE_MESSAGE;
    } else if ( contract.balanceInfo.pastDue && contract.baseContract.clientId !== EMPLYD_CLIENT ) {
        status = BALANCE_DUE_STATUS;
        msg = BALANCE_DUE_MESSAGE;
        makePayment = contract.userPermissions.MAKE_PAYMENT;
    } else if ( contract.balanceInfo.chargeDate && ( contract.balanceInfo.balanceDue > 0 ) ) {
        msg = NEXT_PAYMENT_MESSAGE;
        msgDate = contract.balanceInfo.dueDate ? contract.balanceInfo.dueDate.getTime() : 0;
        editPaymentInfo = contract.userPermissions.UPDATE_PAYMENT_INFO;
    } else {
        msg = contract.balanceInfo.balanceDue === 0 ? NO_DUE_MESSAGE :CHARGES_PAID_MESSAGE;
        editPaymentInfo = contract.userPermissions.UPDATE_PAYMENT_INFO;
    }

    if ( contract.userStatuses.OVERRIDE_PENDING_CANCEL) {
        editPaymentInfo = true;
    }

    if (this.isPlanEMPLYD(contract.baseContract.clientId)){
        editPaymentInfo = this.isPlanEMPLYD(contract.baseContract.clientId);
    }
    return {
        currentBalance: contract.balanceInfo.balanceDue,
        wilPayOn: contract.balanceInfo.chargeDate,
        autoPay_flag: contract.userStatuses.AUTO_PAYMENT,
        autoPay: (( contract.userStatuses.AUTO_PAYMENT) ? 'on' : 'off' ),
        dueDate: contract.balanceInfo.dueDate ?? new Date(),
        status: status,
        message: msg,
        msgDate: msgDate,
        makePayment: makePayment,
        editPaymentInfo: editPaymentInfo,
    };
  }

  updateMaskedCard(value: string) : string {
    let formattedNumber = '';

    if (/^3[47]/.test(value)) {
      // Amex Format (15 digits: ****-******-*****)
      if (value.length <= 4) {
        formattedNumber = value;
      } else if (value.length <= 10) {
        formattedNumber = '****-' + value.slice(4, 10);
      } else {
        formattedNumber = '****-******-' + value.slice(10, 15);
      }
    } else {
      // Default Format (Visa, Mastercard, etc. - 16 digits: ****-****-****-1234)
      if (value.length <= 4) {
        formattedNumber = value;
      } else if (value.length <= 8) {
        formattedNumber = '****-' + value.slice(4, 8);
      } else if (value.length <= 12) {
        formattedNumber = '****-****-' + value.slice(8, 12);
      } else {
        formattedNumber = '****-****-****-' + value.slice(12, 16);
      }
    }
    return formattedNumber;
  }

  convertInnerHtmlWithValues(content: string, valueToBeReplaced: string, value: string): string {
    const regex = new RegExp(`\\b${valueToBeReplaced}\\b`, 'g');
    return content.replace(regex, value);
  }

  setHtmlContent(content: string): string {
    // decoding the html string from liferay response
    const decodedHtml = new DOMParser().parseFromString(content, 'text/html').body.textContent;
    return decodedHtml || '';
  }

  getUnixDateFormYYYYmmdd(str: string): string {
    const y = parseInt(str.slice(0, 4), 10);
    const m = parseInt(str.slice(4, 6), 10) - 1; // Months are zero-indexed
    const d = parseInt(str.slice(6, 8), 10);

    const date = new Date(y, m, d);

    // Check if the provided date is valid
    if (date.getFullYear() === y && date.getMonth() === m && date.getDate() === d) {
        return date.getTime().toString();
    } else {
        return '0';
    }
  }

  getBillingDayFormatForUI(day: string): string {
    if(!day) { return ''; }

    switch(day) {
      case '01':
        return '1st';
      case '02':
        return '2nd';
      case '03':
        return '3rd';
      case '21':
        return '21st';
      case '22':
        return '22nd';
      case '23':
        return '23rd';
      case '31':
        return '31st';
      default:
        return (day[0] == '0' ? day[1]+ 'th' :  day + 'th');
    }
  }

  groupBy(inputList: IAvailabilityDate[] | ISelectedProduct[], property: any): any {
    // not to group the list if array or property is falsy
    if (!inputList || !inputList.length || !property || !property.length) { return inputList; }

    return inputList.reduce((acc: any, obj: any) => {
      const key = obj[property];
      if (!acc[key]) { acc[key] = []; }
      acc[key].push(obj);
      return acc;
    }, {});
  }

  getSafeUrl(email: string): string {
    return email.replace(/\+/g, '%2B');
  }

  /**
   * @description Validates the job number format.
   * @param jobNum 
   * @returns 
   */
  isValidJob(jobNum: string ): boolean {
    const patternOne = /^[a-zA-Z0-9]{10}-[0-9]{1,2}$/;
    const patternTwo = /^[0-9]{8,9}$/;
    const patternThree = /^[a-zA-Z0-9]{10,20}$/;
    return patternOne.test(jobNum) || patternTwo.test(jobNum) || patternThree.test(jobNum);
  }

  getSurchargeTooltipText(products: ISelectedProduct[], contractData: IContractData): string|null {
    if (products.length >= 1 && !this.isPlanSurge(contractData?.baseContract?.coverageGroup)) {
      return this.isPlanBYOP(contractData?.baseContract?.coverageGroup)
        ? BYOP_SURCHARGE_TEXT_FOR_MULTI_ITEMS
        : SURCHARGE_TEXT_FOR_MULTI_ITEMS;
    }
    return contractData?.baseContract?.surchargeDisclosure || null;
  }

  /**
   * Resolves the cancellation date for API calls.
   * Uses scheduledCancellationDate if available; otherwise falls back to today's date.
   */
  getContractCancelDate(contract?: IContractData | null): string {
    const scheduledDate = contract?.baseContract?.scheduledCancellationDate;
    if (scheduledDate) {
      return formatDate(scheduledDate, 'yyyy-MM-dd', 'en');
    }
    return formatDate(new Date(), 'yyyy-MM-dd', 'en');
  }

  getToastMessages(contract: IContractData | null, liferayContent: IOperationItem|null): Record<string, { title: string; message: string, link?: string, linkText?: string }> {
    // retention phone numbers based on the clientId; call instructions are omitted when unavailable
    const contactNumber = liferayContent?.contactNumber || '';
    const contactNumFormatted = this.formatPhone(contactNumber);
    
    const formattedCancelDate = this.formatStrDate(contract?.baseContract?.cancelDate || '');
    const formattedExpirationDate = this.formatStrDate(contract?.baseContract?.expirationDate || '');
    const formattedScheduledCancellationDate = contract?.baseContract?.scheduledCancellationDate ? formatDate(contract?.baseContract?.scheduledCancellationDate, 'MM/dd/YYYY', 'en'):'';
   
    const formedMessages: {[status: string]: { title: string; message: string, link?: string, linkText?: string }} = {
      active: {
        title: CONTRACT_STATUS_ACTIVE,
        message: '',
      },
      inactive: {
        title: CONTRACT_STATUS_INACTIVE,
        message: `Your coverage is not effective until ${this.formatStrDate(contract?.baseContract?.effectiveDate || '')}. We are unable to process a claim for you at this time. We are happy to refer you to a Service Partner in your area. Please contact us for assistance.`
      },
      cancelled: {
        title: CONTRACT_STATUS_TITLE_PLAN_CANCELLED,
        message: !!formattedCancelDate ? `Your plan was cancelled on ${formattedCancelDate}.` : '',
      },
      expired: {
        title: CONTRACT_STATUS_TITLE_PLAN_EXPIRED,
        message: `Your plan expired on ${formattedExpirationDate}.`
      },
      'cancellation scheduled': {
        title: CONTRACT_STATUS_CANCELLATION_SCHEDULED,
        message: formattedScheduledCancellationDate ? `Your plan will be cancelled on ${formattedScheduledCancellationDate}, and your home will be covered through this date.` : '',
      },
    };

    if (!!contactNumber && !!formattedCancelDate && (contract?.baseContract?.clientId !== CLIENT_ID.SEARS) && (this.getDifferenceInDays(formattedCancelDate||'') < ALLOWED_ADD_COVERAGE_DAYS)) {
      formedMessages['cancelled'] = {
        ...formedMessages['cancelled'],
        message: `Your plan was cancelled on ${formattedCancelDate}. To add coverage, please call`,
        link: contactNumber || '',
        linkText: contactNumFormatted || '',
      }
    }

    if (!!contactNumber && !!formattedExpirationDate && (contract?.baseContract?.clientId !== CLIENT_ID.SEARS) && (this.getDifferenceInDays(formattedExpirationDate||'') < ALLOWED_ADD_COVERAGE_DAYS)) {
      formedMessages['expired'] = {
        ...formedMessages['expired'],
        message: `Your plan expired on ${formattedExpirationDate}. To add coverage, please call`,
        link: contactNumber || '',
        linkText: contactNumFormatted || '',
      }
    }

    if (!!contactNumber && !!formattedScheduledCancellationDate && (contract?.baseContract?.clientId !== CLIENT_ID.SEARS)) {
      formedMessages['cancellation scheduled'] = {
        ...formedMessages['cancellation scheduled'],
        message: `Your plan will be cancelled on ${formattedScheduledCancellationDate}, and your home will be covered through this date. To keep your coverage, please call`,
        link: contactNumber || '',
        linkText: contactNumFormatted || '',
      };
    }

    return formedMessages;
  }

  getDifferenceInDays(date: string): number {
    if (!date) {
      return 0; // Return -1 if date is not provided
    }
    const today = new Date();
    const pastDate = new Date(date);
    const diffInValue = today.valueOf()-pastDate.valueOf();
    // handle the negative difference and positive difference
    return diffInValue > 0  ? Math.floor(diffInValue / (1000 * 60 * 60 * 24)) : 0;
  }

  sanitizeErrorMessage(error: string): string {
    const rawMessage = (error || 'An unexpected error occurred').toString();

    return rawMessage
      .replace(/<[^>]+>/g, '')          // Strip HTML tags
      .replace(/\d{3,}/g, '[redacted]') // Redact long numbers (3+ digits)
      .trim();
  }
  /**
   * @description Returns the plan type based on the contract data.
   * @param contractData 
   * @returns 
   */
  getPlanType(contractData: IContractData): string {
  if (!contractData?.baseContract) {
    return 'Default'; // Return a default value if baseContract is not available;
  }

  const { coverageGroup, clientId } = contractData.baseContract;

  if (coverageGroup === SRG) {
    return 'Cinch_SRG'; 
  }

  if (coverageGroup === TXSRG) {
    return 'Cinch_TXSRG'; 
  }

  if (this.isPlanTxuCE(coverageGroup)) {
    return 'Cinch_TXUCE';
  }

  if (this.isPlanBYOP(coverageGroup)) {
    return 'Cinch_BYOP';
  }

  if (this.isPlanCE(coverageGroup)) {
    return 'Cinch_CE';
  }

  if (coverageGroup === 'N') {
    // Coverage group not present → fall back to clientId
    switch (clientId?.toUpperCase()) {
      case 'SEARS':
        return 'Sears_plan';
      case 'HMS':
        return 'HMS_plan';
      case 'EMPLYD':
        return 'Employee_Plan';
      case 'TXU':
        return 'TXU_plan';
      default:
        return clientId ? `${clientId}_plan` : 'Default';
    }
  }

  // Fallback if nothing matched
  return 'Default';
}

  isPreventiveMaintenance(models: Model[]): boolean {
    return models?.some(
      (model: Partial<Model>) =>
        typeof model.serviceRequestType === 'string' &&
        model.serviceRequestType.toLowerCase() === TEXT_PREVENTATIVE_MAINTENANCE
    ) ?? false;
  }

  isArchMIYearZero(contract: IContract | null | undefined): boolean {
    const clientId = contract?.clientId?.toUpperCase();
    const contractYear = contract?.contractYear;
    return clientId === ARCHMI && contractYear === '0';
  }

  isAllStateYearZero(contract: IContract | null | undefined): boolean {
    const clientId = contract?.clientId?.toUpperCase();
    const contractYear = contract?.contractYear;
    return clientId === ALLSTATE && contractYear === '0';
  }

  isPlanBillMechCodeEmbed(billMechCode: string | null | undefined): boolean {
    if (!billMechCode) return false;
    return (MADE_CREDIT_CARD_ENABLED)
      && billMechCode.toUpperCase() === EMBED;
  }

  isThirdPartyBilling(billMechCode: string | null | undefined): boolean {
    if (!billMechCode) return false;
    return billMechCode.toUpperCase() === EMBED || billMechCode.toUpperCase() === BILLONCE || billMechCode.toUpperCase() === BILLCLIENT;
  }

  isCCOrACH(billMechCode: string | null | undefined): boolean {
    if (!billMechCode) return false;
    return billMechCode.toUpperCase() === PAYMENTECH || billMechCode.toUpperCase() === ACH_AUTO;
  }

  isPlanBillMechCodeBillClient(billMechCode: string | null | undefined): boolean {
    if (!billMechCode) return false;
    return (MADE_CREDIT_CARD_ENABLED) 
      && billMechCode.toUpperCase() === BILLCLIENT;
  }

  getHours(isoDate: string): number {
    const scheduledDate = new Date(isoDate);
    const now = new Date();

    const diffInMs = scheduledDate.getTime() - now.getTime();
    const diffInHours = diffInMs / (1000 * 60 * 60);
    return Math.floor(diffInHours);
  }

  getHoursFromNow(timestamp: number): number {
    return Math.floor((timestamp - Date.now()) / (1000 * 60 * 60));
  }

  showPaymentMethodAlert(contractData: IContractData): boolean {
    const isThirdPartyBilling = this.isThirdPartyBilling(contractData.baseContract.billMechCode);
    // checking the billMechCode for both current and renewal contract to cover the scenario
    // where user has renewed the plan and the billMechCode has been changed to or from credit card or ACH in the renewal contract
    const isCCOrACH = this.isCCOrACH(contractData.baseContract.billMechCode) || this.isCCOrACH(contractData.renewalContract?.billMechCode);
    const isArchMIYearZero = this.isArchMIYearZero(contractData.baseContract);
    const isAllStateYearZero = this.isAllStateYearZero(contractData.baseContract);
    const balanceDue = contractData.balanceInfo.balanceDue > 0 || contractData.balanceInfo.pastDue === true;
    const isPaidInFullYearZero = isArchMIYearZero || isAllStateYearZero;

    // Do NOT show for these conditions
    if (isThirdPartyBilling) return false;
    if (isPaidInFullYearZero) return false;
    if (isCCOrACH) return false;
    if (balanceDue) return false;

    // Show for everyone else
    return true;
  }

  /**
   * Resolves the operation item for the client. MOEN clients fall back to the
   * feature-off item when the MOEN/H4H feature flag is disabled.
   */
  resolveOperationItem(
    operationItems: IOperationItem[] | null,
    clientId: string,
    featureFlags: IFeatureFlagsPerEnvironment | null
  ): IOperationItem | null {
    const moenAndH4hFlag = featureFlags?.features
      ?.find((feature: IFeatureFlag) => feature.feature === FEATURES_CONSTANT.MOENANDH4H)?.flag;
    const clientIdToUse = (!moenAndH4hFlag && clientId === MOEN_CLIENT) ? LIFERAY_FEATURE_OFF : clientId;
    return this.getOperationItem(operationItems, clientIdToUse);
  }

  /**
   * Overrides the hours of operation text with the state-level Liferay item
   * (feature 'stateClient_<STATE>', e.g. 'stateClient_CA') when one exists.
   * RE (HMS), MadeCC and Moen clients keep their client-specific hours.
   */
  applyStateOperationHours(
    operationItem: IOperationItem | null,
    operationItems: IOperationItem[] | null,
    clientId: string,
    state: string | null | undefined
  ): IOperationItem | null {
    const isExcludedClient = [HMS_CLIENT, CLIENT_MADECC, MOEN_CLIENT].includes(clientId);
    if (!operationItem || !state || isExcludedClient) {
      return operationItem;
    }
    const stateItem = operationItems?.find(item => item.feature === `${STATE_CLIENT_LIFERAY_FEATURE_PREFIX}${state}`);
    if (!stateItem?.operationHoursText) {
      return operationItem;
    }
    return { ...operationItem, operationHoursText: stateItem.operationHoursText };
  }

  getOperationItem(liferayContent: IOperationItem[] | null, clientId: string): IOperationItem | null {
    if (!liferayContent || !clientId) {
      return null;
    }
    
    switch (clientId) {
      case HMS_CLIENT:
        return liferayContent?.find(item => item.feature === HMS_CLIENT_LIFERAY_FEATURE) || null;
      case CLIENT_MADECC:
        return liferayContent?.find(item => item.feature === MADE_CREDIT_CARD_LIFERAY_FEATURE) || null;
      case MOEN_CLIENT:
        return liferayContent?.find(item =>item.feature === MOEN_LIFERAY_FEATURE) || null;
        case SIRVA_CLIENT:
        return liferayContent?.find(item => item.feature === SIRVA_LIFERAY_FEATURE) || null;
      default:
        return liferayContent?.find(item => item.feature === NORMAL_CLIENT_LIFERAY_FEATURE) || null;
    }
  }

  public getShowCancelRequestButtonUI(
    product: { serviceProviderAccountNumber: string; cancelable: boolean; serviceJobStatus?: string | null; serviceJobSubStatus?: string | null },
    isPlanBYOP: boolean,
    isPlanSurge: boolean
  ): boolean {
    const isAPOSPjob = this.isAPOSPJob(product.serviceProviderAccountNumber);

    if (this.isRoofLeakJob(product.serviceProviderAccountNumber)) {
      return false;
    }

    if (this.isProcureVendorJob(product.serviceProviderAccountNumber) &&
      (product.serviceJobStatus === CMP || product.serviceJobStatus === CAN)) {
      return false;
    }

    if (isAPOSPjob) {
      if (product.serviceJobStatus === CAN || product.serviceJobStatus === CMP) return false;
      return APOSP_CANCEL_BUTTON_ENABLED && product.cancelable && product.serviceJobSubStatus !== 'JPA';
    }

    // BAU for non-APOSP
    return !isAPOSPjob && !isPlanBYOP && !isPlanSurge && product.cancelable && !!product.serviceJobStatus;
  }

  public getProcureVendorStatusMessage(displayStatusCode?: string | null): string {
    if (displayStatusCode === SRCMPD) {
      return COMPLETED_MESSAGE;
    }
    if (displayStatusCode === SRCAND) {
      return CANCELLED_MESSAGE;
    }
    return PROCURE_VENDOR_MESSAGE;
  }

  public getShowServiceAgainButtonUI(
    product: { serviceProviderAccountNumber: string; isEligibleForRecall: boolean } & IADServiceAgainJob,
    isPlanBYOP: boolean,
    isPlanSurge: boolean
  ): boolean {
    const isAPOSPjob = this.isAPOSPJob(product.serviceProviderAccountNumber);

    if (this.isRoofLeakJob(product.serviceProviderAccountNumber)) {
      return false;
    }

    if (this.isServiceAgainRestrictedForADJob(product.serviceProviderAccountNumber, product)) {
      return false;
    }

    if (isAPOSPjob) {
      return product.serviceJobStatus === CMP && !isPlanBYOP && !isPlanSurge && product.isEligibleForRecall;
    }

    return !isPlanBYOP && !isPlanSurge && product.isEligibleForRecall;
  }

  /**
   * @description Whether the Perks & Benefits page is available to the user (menu link and dashboard card)
   * @param contract
   * @returns boolean
   */
  public isPerksBenefitsVisible(contract?: IContractData | null): boolean {
    if (!contract?.baseContract?.contractStatus) return false;

    const contractStatus = contract.baseContract.contractStatus.toLowerCase();
    const isPlanStatusIncluded = [
      CONTRACT_STATUS_UPPER_EXPIRED.toLowerCase(),
      CONTRACT_STATUS_UPPER_INACTIVE.toLowerCase(),
      CONTRACT_STATUS_UPPER_CANCELED.toLowerCase(),
      CONTRACT_STATUS_CANCELLED.toLowerCase()
    ].includes(contractStatus);
    const { sears, ghp, ghs } = ClientIdForLogos;

    return !isPlanStatusIncluded
      && !this.isPlanCE(contract.baseContract.coverageGroup)
      && ![sears, ghp, ghs].includes(contract.baseContract.clientId);
  }

  public checkSpecialBenefits(contract?: IContractData | null): boolean {
    if (!contract?.productBaseCoverage?.baseItems) return false;

    // Check base items
    const hasBaseItem = contract.productBaseCoverage.baseItems.some(
      (item) => SPECIAL_BENEFITS_CONTACT_NUMBERS[item.itemCode]
    );
    if (hasBaseItem) return true;

    // Check special cases
    const productName = contract.productBaseCoverage?.productInfo?.name || '';
    const coverageGroup = contract.baseContract?.coverageGroup || '';
    if (productName.includes(HOME_ELECTRONICS) || productName.includes(HOME_ELCTRNCS)) {
      if (coverageGroup === CE_PLAN || coverageGroup === TXUCE_PLAN) {
        return true;
      }
    }

    return false;
  }

  public checkConfirmContactEligibility(contractData?: IContractData | null): boolean {
    const { baseContract, contact } = contractData || {};
    if (!baseContract) return false;

    const isEligibleContract =
      baseContract.clientId === HMS_CLIENT &&
      baseContract.contractYear === '0' &&
      (baseContract.purchaseTypeCode === BCW || baseContract.purchaseTypeCode === BDW) &&
      (baseContract.paymentCount === 0 || baseContract.paymentCount === null);

    const phoneList = contact?.phoneList;
    const hasOnlyCSVKCodes =
      !phoneList ||
      phoneList.length === 0 ||
      phoneList.every(
        (phone) => CSVK_CODES.includes(phone.createdByModule ?? '') && phone.objectVersionNumber === '1'
      );

    return isEligibleContract && hasOnlyCSVKCodes;
  }

  public parseDateToTimestamp(dateVal: string | number | null | undefined): number {
    if (typeof dateVal === 'number') {
      return dateVal;
    }
    if (!dateVal) return 0;
    const strVal = String(dateVal).trim();
    if (/^\d{8}$/.test(strVal)) {
      const year = parseInt(strVal.substring(0, 4), 10);
      const month = parseInt(strVal.substring(4, 6), 10) - 1;
      const day = parseInt(strVal.substring(6, 8), 10);
      const parsed = new Date(year, month, day).getTime();
      return isNaN(parsed) ? 0 : parsed;
    }
    if (/^\d{9,}$/.test(strVal)) {
      return Number(strVal);
    }
    const parsed = new Date(strVal).getTime();
    return isNaN(parsed) ? 0 : parsed;
  }

  public getJobTimestamp(job: Partial<IApiClaimsHistory> | null | undefined): number {
    if (!job) return 0;
    if (job.completionDate && typeof job.completionDate === 'number') {
      return job.completionDate;
    }
    if (job.completionDateFormat) {
      const parsed = this.parseDateToTimestamp(job.completionDateFormat);
      if (parsed > 0) return parsed;
    }
    if (job.requestDate && typeof job.requestDate === 'number') {
      return job.requestDate;
    }
    if (job.requestDateFormatUS) {
      const parsed = this.parseDateToTimestamp(job.requestDateFormatUS);
      if (parsed > 0) return parsed;
    }
    if (job.serviceDate && typeof job.serviceDate === 'number') {
      return job.serviceDate;
    }
    if (job.serviceJobDate) {
      const parsed = this.parseDateToTimestamp(job.serviceJobDate);
      if (parsed > 0) return parsed;
    }
    return 0;
  }

  public isJobWithin180Days(timestamp: number): boolean {
    if (timestamp <= 0) {
      return true;
    }
    const oneHundredEightyDaysMs = 180 * 24 * 60 * 60 * 1000;
    return (Date.now() - timestamp) <= oneHundredEightyDaysMs;
  }
}